import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { uploadProductImage } from "@/utils/imageUpload";

interface AddPhotoButtonProps {
  productId: string;
  productBarcode: string;
  onPhotoAdded: (photoUrl: string) => void;
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "default" | "lg";
}

export const AddPhotoButton = ({
  productId,
  productBarcode,
  onPhotoAdded,
  variant = "outline",
  size = "sm"
}: AddPhotoButtonProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    
    try {
      console.log('📸 Adding photo to existing product:', { productId, productBarcode, fileName: file.name });
      
      const result = await uploadProductImage(file, productId);
      
      if (result.success) {
        console.log('✅ Photo added successfully:', result.url);
        onPhotoAdded(result.url);
        
        toast({
          title: "Photo Added",
          description: "Photo has been uploaded successfully",
        });
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('❌ Photo upload error:', error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload photo",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      <Button
        onClick={handleFileSelect}
        variant={variant}
        size={size}
        disabled={isUploading}
        className="gap-2"
      >
        {isUploading ? (
          <Upload className="w-4 h-4 animate-spin" />
        ) : (
          <Camera className="w-4 h-4" />
        )}
        {isUploading ? "Uploading..." : "Add Photo"}
      </Button>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />
    </>
  );
};